import {
  type EmailPreviewGroup,
  EMAIL_PREVIEW_GROUPS,
  EMAIL_PREVIEW_REGISTRY,
  emailPreviewGroupSlug,
  emailPreviewSlug
} from '@tandemone/types'
import {
  type LucideIcon,
  BadgeCheck,
  BarChart3,
  Brain,
  ClipboardCheck,
  CreditCard,
  Factory,
  Files,
  FileText,
  Inbox,
  Network,
  Receipt,
  Settings,
  ShieldCheck,
  ShoppingCart,
  TrendingUp,
  UserCog,
  Users
} from 'lucide-react'

export type NavItem = {
  label: string
  icon: LucideIcon
  href: string
  children?: NavChild[]
  /** hidden unless the active company holds a supplier role. */
  supplierOnly?: boolean
  /** hidden until the paywall is switched on, so the surface ships dark. */
  billingOnly?: boolean
  /** the ids the page keeps in ?tab=. the first is where it lands, so a page
   *  view can name the tab even when the address leaves it out. */
  tabs?: readonly string[]
}

export type NavChild = {
  label: string
  icon: LucideIcon
  href: string
}

export type NavGroup = {
  label: string
  items: NavItem[]
  superOnly?: boolean
}

/** account settings are the settings page, so this is the bare path. shared
 *  with the shell, which fills this item's children in at render time.
 */
export const SETTINGS_NAV_HREF = '/settings'

/** billing sits beside settings rather than inside it: it is money, not a
 *  preference, and it carries three tabs of its own. */
export const BILLING_NAV_HREF = '/billing'

/** these rows carry nav badges, so the badge map and the nav row cannot drift
 *  apart on a path string. */
export const DUE_DILIGENCE_NAV_HREF = '/super/due-diligence'
export const CERTIFICATE_REVIEW_NAV_HREF = '/super/certificate-review'
export const RFI_NAV_HREF = '/rfi'

export const EMAIL_TEMPLATES_NAV_HREF = '/super/email-templates'

export function emailPreviewHref(group: EmailPreviewGroup) {
  return `${EMAIL_TEMPLATES_NAV_HREF}/${emailPreviewGroupSlug(group)}`
}

export const SUPPLIER_TABS = [
  'all',
  'founding',
  'verified',
  'favorites'
] as const
export const RFI_TABS = ['sent', 'received'] as const
export const INVOICE_TABS = ['sent', 'received'] as const
export const INTELLIGENCE_TABS = ['metals', 'rates'] as const
export const BILLING_TABS = [
  'plan',
  'payment-method',
  'payment-history'
] as const
export const DUE_DILIGENCE_TABS = ['pending', 'hidden'] as const

export function emailTemplateTabs(group: EmailPreviewGroup): string[] {
  return EMAIL_PREVIEW_REGISTRY.filter(meta => meta.group === group).map(meta =>
    emailPreviewSlug(meta.label)
  )
}

const EMAIL_GROUP_ICON: Record<EmailPreviewGroup, LucideIcon> = {
  Auth: ShieldCheck,
  Onboarding: BadgeCheck,
  Billing: Receipt,
  RFI: Inbox,
  Invoice: FileText
}

const EMAIL_NAV_ITEMS: NavItem[] = EMAIL_PREVIEW_GROUPS.map(group => ({
  label: group,
  icon: EMAIL_GROUP_ICON[group],
  href: emailPreviewHref(group),
  tabs: emailTemplateTabs(group)
}))

/* only Settings keeps a dropdown. every other section splits its pages into
 * tabs on the page itself. */
export const NAV_GROUPS: NavGroup[] = [
  /* notifications and messages sit above all of this in the header's
   * SidebarQuickNav, not here. */
  {
    label: 'Platform',
    superOnly: true,
    items: [
      { label: 'Metrics', icon: BarChart3, href: '/super/platform-metrics' },
      {
        label: 'Due Diligence',
        icon: ShieldCheck,
        href: DUE_DILIGENCE_NAV_HREF,
        tabs: DUE_DILIGENCE_TABS
      },
      {
        label: 'Certificate Review',
        icon: ClipboardCheck,
        href: CERTIFICATE_REVIEW_NAV_HREF
      },
      { label: 'Suppliers', icon: Factory, href: '/super/suppliers' },
      { label: 'Buyers', icon: ShoppingCart, href: '/super/buyers' },
      { label: 'Users', icon: UserCog, href: '/super/users' },
      { label: 'RFIs', icon: FileText, href: '/super/rfis' },
      {
        label: 'Billing',
        icon: CreditCard,
        href: '/super/billing',
        billingOnly: true
      }
    ]
  },
  {
    label: 'Email Templates',
    superOnly: true,
    items: EMAIL_NAV_ITEMS
  },
  {
    label: 'Sourcing',
    items: [
      {
        label: 'Supplier Network',
        icon: Network,
        href: '/suppliers',
        tabs: SUPPLIER_TABS
      },
      {
        label: 'TandemOne RFI',
        icon: FileText,
        href: RFI_NAV_HREF,
        tabs: RFI_TABS
      }
    ]
  },
  {
    label: 'Platform',
    items: [
      {
        label: 'TandemOne Insights',
        icon: TrendingUp,
        href: '/insights',
        supplierOnly: true
      },
      {
        label: 'TandemOne Intelligence',
        icon: Brain,
        href: '/intelligence',
        tabs: INTELLIGENCE_TABS
      },
      {
        label: 'Invoice Tracking System',
        icon: Receipt,
        href: '/invoices',
        supplierOnly: true,
        tabs: INVOICE_TABS
      }
    ]
  },
  {
    label: 'Company',
    items: [
      {
        label: 'Employees',
        icon: Users,
        href: '/employees',
        supplierOnly: true
      },
      {
        label: 'Documents',
        icon: Files,
        href: '/documents',
        supplierOnly: true
      },
      {
        label: 'Certificates',
        icon: BadgeCheck,
        href: '/certificates',
        supplierOnly: true
      },
      {
        label: 'Billing',
        icon: CreditCard,
        href: BILLING_NAV_HREF,
        supplierOnly: true,
        billingOnly: true,
        tabs: BILLING_TABS
      },
      { label: 'Settings', icon: Settings, href: SETTINGS_NAV_HREF }
    ]
  }
]

/** what this viewer actually sees, with every visibility rule in one place.
 *
 *  settingsChildren comes from the caller because it depends on setup state the
 *  static nav has no access to.
 */
export function visibleNavGroups(
  groups: NavGroup[],
  opts: {
    inSuperScope: boolean
    isSupplier: boolean
    billingEnabled: boolean
    settingsChildren: NavChild[]
  }
): NavGroup[] {
  /* the two sets are disjoint: Super shows only super tools, a company shows
   * only company tools. stacking both is what made the super view crowded. */
  return groups
    .filter(group =>
      opts.inSuperScope ? group.superOnly === true : group.superOnly !== true
    )
    .map(group => ({
      ...group,
      items: group.items
        .filter(item => !item.supplierOnly || opts.isSupplier)
        .filter(item => !item.billingOnly || opts.billingEnabled)
        .map(item =>
          item.href === SETTINGS_NAV_HREF
            ? { ...item, children: opts.settingsChildren }
            : item
        )
        /* a group whose only child is its own page is not a group: the
         * dropdown would open onto the row it hangs from. render it as a plain
         * tab instead. */
        .map(item => {
          const distinct = item.children?.filter(
            child => child.href !== item.href
          )
          return distinct?.length ? item : { ...item, children: undefined }
        })
    }))
}

/** the crumb trail for a path, so a grouped page reads "Supplier Network >
 *  All Suppliers" without every page restating its own parent.
 *
 *  matches exactly, never by prefix: /suppliers/dunder-mifflin is a detail
 *  page, not a nav destination, and must fall through to the title its own
 *  page supplies.
 */
export function findNavTrail(
  groups: NavGroup[],
  pathname: string
): { label: string; to?: string }[] | null {
  for (const group of groups) {
    for (const item of group.items) {
      const child = item.children?.find(entry => entry.href === pathname)
      if (child)
        return [{ label: item.label, to: item.href }, { label: child.label }]
      if (item.href === pathname) return [{ label: item.label }]
    }
  }
  return null
}

/** exact match, like findNavTrail: a detail page has no tabs of its own. */
export function navTabsFor(pathname: string): readonly string[] | null {
  for (const group of NAV_GROUPS) {
    for (const item of group.items) {
      if (item.href === pathname) return item.tabs ?? null
    }
  }
  return null
}
